import { BusinessObjectBase } from './base';
import { activityMappingSchema } from '@schemas/business-objects/activity-mapping';
import type { Tenant } from './tenant';
import type { Id, MappingAction, Uuid } from '../base-types';

export interface ActivityMappingData {
  readonly id: Id;
  readonly designUuid: Uuid;
  readonly sourceActivityId: string;
  readonly action: MappingAction;
  readonly targetActivityId?: string;
  readonly minSourceVersion?: number;
  readonly maxSourceVersion?: number;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
}

export interface ActivityMapping extends Readonly<ActivityMappingData> {}
type ActivityMappingCreateWithChildrenData = Partial<ActivityMappingData>;

export class ActivityMapping extends BusinessObjectBase {
  static readonly entityName = 'ActivityMapping' as const;
  static readonly plural = 'activity-mappings';
  static schema = activityMappingSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ActivityMapping[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ActivityMapping(data));
  }

  static async findOne(filter?: any): Promise<ActivityMapping | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ActivityMapping(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ActivityMapping> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ActivityMapping(data);
  }

  static async create(
    data: Omit<ActivityMappingData, 'id'>,
  ): Promise<ActivityMapping> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ActivityMapping(result);
  }

  static async createMany(
    data: Omit<ActivityMappingData, 'id'>[],
  ): Promise<ActivityMapping[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ActivityMapping(item));
  }

  static async createWithChildren(
    data: ActivityMappingCreateWithChildrenData,
  ): Promise<ActivityMapping> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ActivityMapping(result);
  }

  static async createManyWithChildren(
    data: ActivityMappingCreateWithChildrenData[],
  ): Promise<ActivityMapping[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ActivityMapping(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ActivityMappingData>,
  ): Promise<ActivityMapping> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ActivityMapping(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
